import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { profileService, UserProfile } from '../services/profileService';
import { Star } from 'lucide-react';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './Profile.css';

const Profile: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile>({
    dob: '',
    time_of_birth: '',
    place_of_birth: '',
    zodiac_sign: ''
  });

  const searchParams = new URLSearchParams(location.search);
  const isOnboarding = searchParams.get('onboarding') === 'true';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchProfile = async () => {
      try {
        const data = await profileService.getProfile();
        if (data) {
          setProfile(data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newProfile = { ...profile, [name]: value };
    
    if (name === 'dob') {
      newProfile.zodiac_sign = profileService.calculateZodiacSign(value);
    }
    
    setProfile(newProfile);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const success = await profileService.updateProfile(profile);
      if (success) {
        if (isOnboarding) {
          navigate('/horoscope');
        } else {
          alert('Profile saved successfully!');
        }
      } else {
        alert('Failed to save profile. Please try again.');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <h1>{isOnboarding ? 'Complete Your Profile' : 'Your Astrological Profile'}</h1>
          <p>{isOnboarding ? 'Help us personalize your astrological insights.' : 'Update your birth details to get accurate readings.'}</p>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="dob">Date of Birth</label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={profile.dob}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="time_of_birth">Time of Birth</label>
            <input
              type="time"
              id="time_of_birth"
              name="time_of_birth"
              value={profile.time_of_birth}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="place_of_birth">Place of Birth</label>
            <input
              type="text"
              id="place_of_birth"
              name="place_of_birth"
              value={profile.place_of_birth}
              onChange={handleChange}
              placeholder="e.g. New Delhi, India"
              required
            />
          </div>

          {profile.zodiac_sign && (
            <div className="form-group">
              <label>Your Zodiac Sign</label>
              <div className="zodiac-display">
                <Star size={20} />
                <span>{profile.zodiac_sign}</span>
              </div>
            </div>
          )}

          <button type="submit" className="save-button" disabled={saving}>
            {saving ? 'Saving...' : (isOnboarding ? 'Continue to App' : 'Save Profile')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Profile;
